import { useEffect, useCallback, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { fetchDayDataForDateRange } from "../services/operations/operationsQueryService";
import {
  ViewType,
  getPreviousWeekStart,
  getPreviousMonthAnchor,
  getPreviousQuarterAnchor,
  getPreviousYearAnchor,
  getWeekEnd,
  getMonthRange,
  getQuarterRange,
  getYearRange,
  shiftAnchor,
  isAnchorCurrentPeriod,
  formatWeekRange,
  formatMonthLabel,
  formatQuarterLabel,
  formatYearLabel,
  toLocalDateString,
} from "../utils/weekUtils";
import {
  HistoryMachineType,
  HISTORY_MACHINE_TYPE_FILTERS,
  DEFAULT_HISTORY_MACHINE_TYPE,
} from "../constants/historyMachineTypeFilters";
import { DayData } from "../types";

export type { ViewType };

const VALID_VIEW_TYPES: ViewType[] = ["Weekly", "Monthly", "Quarterly", "Yearly"];

// =============================================
// State shape returned by this hook.
// =============================================
export interface UseHistoricalDataResult {
  dayData: DayData[];
  isLoading: boolean;
  fetchError: string | null;
  periodStart: Date;
  periodEnd: Date;
  periodLabel: string;
  isCurrentPeriod: boolean;
  viewType: ViewType;
  setViewType: (viewType: ViewType) => void;
  selectedMachineType: HistoryMachineType;
  selectMachineType: (machineType: HistoryMachineType) => void;
  goToPrevious: () => void;
  goToNext: () => void;
}

// =============================================
// Returns the default anchor for a given view
// type, always set to the previous period so
// the page does not open on an in-progress period.
// =============================================
function getDefaultAnchor(viewType: ViewType): Date {
  switch (viewType) {
    case "Weekly":
      return getPreviousWeekStart();
    case "Monthly":
      return getPreviousMonthAnchor();
    case "Quarterly":
      return getPreviousQuarterAnchor();
    case "Yearly":
      return getPreviousYearAnchor();
  }
}

// =============================================
// Parses and validates the `view` query param.
// Falls back to "Weekly" for missing/invalid values.
// =============================================
function parseViewType(raw: string | null): ViewType {
  if (raw && (VALID_VIEW_TYPES as string[]).includes(raw)) {
    return raw as ViewType;
  }
  return "Weekly";
}

// =============================================
// Parses and validates the `date` query param.
// Expects YYYY-MM-DD. Falls back to the default
// anchor for the given view type.
// =============================================
function parseAnchor(raw: string | null, viewType: ViewType): Date {
  if (raw) {
    const parsed = new Date(`${raw}T00:00:00`);
    if (!isNaN(parsed.getTime())) {
      return parsed;
    }
  }
  return getDefaultAnchor(viewType);
}

// =============================================
// Parses and validates the `machine` query param.
// Falls back to the default machine type.
// =============================================
function parseMachineType(raw: string | null): HistoryMachineType {
  if (raw && (HISTORY_MACHINE_TYPE_FILTERS as string[]).includes(raw)) {
    return raw as HistoryMachineType;
  }
  return DEFAULT_HISTORY_MACHINE_TYPE;
}

// =============================================
// Derives the date range and display label for
// the period that contains anchor.
// =============================================
function getPeriodBounds(
  anchor: Date,
  viewType: ViewType
): { periodStart: Date; periodEnd: Date; periodLabel: string } {
  switch (viewType) {
    case "Weekly": {
      const periodEnd = getWeekEnd(anchor);
      return {
        periodStart: anchor,
        periodEnd,
        periodLabel: formatWeekRange(anchor, periodEnd),
      };
    }
    case "Monthly": {
      const { start, end } = getMonthRange(anchor);
      return { periodStart: start, periodEnd: end, periodLabel: formatMonthLabel(anchor) };
    }
    case "Quarterly": {
      const { start, end } = getQuarterRange(anchor);
      return { periodStart: start, periodEnd: end, periodLabel: formatQuarterLabel(anchor) };
    }
    case "Yearly": {
      const { start, end } = getYearRange(anchor);
      return { periodStart: start, periodEnd: end, periodLabel: formatYearLabel(anchor) };
    }
  }
}

// =============================================
// Manages the selected period (week / month /
// quarter / year) and fetches aggregated data
// for that range.
//
// State is persisted in URL query params:
//   ?view=Weekly&date=2026-04-28&machine=CNC
//
// This lets the user refresh or share the URL
// and land on exactly the same view. All params
// are validated on parse and fall back to sensible
// defaults so malformed URLs degrade gracefully.
//
// replace: true is used on all setSearchParams
// calls so stepping through periods does not
// pollute the browser history stack.
// =============================================
export function useHistoricalData(): UseHistoricalDataResult {
  const [searchParams, setSearchParams] = useSearchParams();

  const viewType = parseViewType(searchParams.get("view"));
  const anchor = parseAnchor(searchParams.get("date"), viewType);
  const selectedMachineType = parseMachineType(searchParams.get("machine"));

  const [dayData, setDayData] = useState<DayData[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [fetchError, setFetchError] = useState<string | null>(null);

  const { periodStart, periodEnd, periodLabel } = getPeriodBounds(anchor, viewType);
  const isCurrentPeriod = isAnchorCurrentPeriod(anchor, viewType);

  // =============================================
  // Write all three params at once, preserving
  // any that are not explicitly overridden.
  // =============================================
  const updateParams = useCallback(
    (overrides: { view?: ViewType; date?: string; machine?: HistoryMachineType }) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (overrides.view !== undefined) next.set("view", overrides.view);
          if (overrides.date !== undefined) next.set("date", overrides.date);
          if (overrides.machine !== undefined) next.set("machine", overrides.machine);
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  useEffect(() => {
    let isCancelled = false;

    async function loadData(): Promise<void> {
      setIsLoading(true);
      setFetchError(null);

      try {
        const startDate = toLocalDateString(periodStart);
        const endDate = toLocalDateString(periodEnd);
        const result = await fetchDayDataForDateRange(
          startDate,
          endDate,
          selectedMachineType
        );

        if (!isCancelled) {
          setDayData(result);
        }
      } catch (error) {
        if (!isCancelled) {
          const message =
            error instanceof Error
              ? error.message
              : "An unexpected error occurred while loading historical data.";
          setFetchError(message);
        }
      } finally {
        if (!isCancelled) {
          setIsLoading(false);
        }
      }
    }

    loadData();

    return () => {
      isCancelled = true;
    };
  // periodEnd is derived from anchor+viewType; the serialised params are the true dependencies.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const goToPrevious = useCallback(() => {
    const next = shiftAnchor(anchor, viewType, -1);
    updateParams({ date: toLocalDateString(next) });
  }, [anchor, viewType, updateParams]);

  const goToNext = useCallback(() => {
    if (isAnchorCurrentPeriod(anchor, viewType)) return;
    const next = shiftAnchor(anchor, viewType, 1);
    updateParams({ date: toLocalDateString(next) });
  }, [anchor, viewType, updateParams]);

  const setViewType = useCallback(
    (newViewType: ViewType): void => {
      const defaultAnchor = getDefaultAnchor(newViewType);
      updateParams({ view: newViewType, date: toLocalDateString(defaultAnchor) });
    },
    [updateParams]
  );

  const selectMachineType = useCallback(
    (machineType: HistoryMachineType): void => {
      updateParams({ machine: machineType });
    },
    [updateParams]
  );

  return {
    dayData,
    isLoading,
    fetchError,
    periodStart,
    periodEnd,
    periodLabel,
    isCurrentPeriod,
    viewType,
    setViewType,
    selectedMachineType,
    selectMachineType,
    goToPrevious,
    goToNext,
  };
}
